/**
 * Communication Board Action Bar Configuration Types and Storage
 * 
 * Handles customization of the top action row visibility/order in Communication Board
 */

export interface CommunicationActionItem {
  id: string;
  labelHe: string;
  labelEn: string;
  iconName: string;
  enabled: boolean;
  order: number;
}

export interface CommunicationActionBarConfig {
  actionItems: CommunicationActionItem[];
  updatedAt: string;
}

// Default action items for Communication Board top row
const DEFAULT_ACTION_ITEMS: CommunicationActionItem[] = [
  { id: "deleteWord", labelHe: "מחק מילה", labelEn: "Delete Word", iconName: "Delete", enabled: true, order: 0 },
  { id: "speak", labelHe: "דיבור", labelEn: "Speak", iconName: "Volume2", enabled: true, order: 1 },
  { id: "clear", labelHe: "נקה", labelEn: "Clear", iconName: "Trash2", enabled: true, order: 2 },
  { id: "pageToggle", labelHe: "מקלדת", labelEn: "Keyboard", iconName: "Keyboard", enabled: true, order: 3 },
  { id: "send", labelHe: "שליחה", labelEn: "Send", iconName: "Mail", enabled: true, order: 4 },
];

const STORAGE_KEY = "communicationActionBarConfig_v1";

// Load config from localStorage
export function loadCommunicationActionBarConfig(): CommunicationActionBarConfig {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const config: CommunicationActionBarConfig = JSON.parse(saved);
      // Ensure all default items exist (for forward compatibility)
      const existingIds = config.actionItems.map(i => i.id);
      const missingItems = DEFAULT_ACTION_ITEMS.filter(d => !existingIds.includes(d.id));
      if (missingItems.length > 0) {
        config.actionItems = [...config.actionItems, ...missingItems];
      }
      return config;
    }
  } catch (e) {
    console.error("Failed to load communication action bar config:", e);
  }
  
  // Return default
  return {
    actionItems: [...DEFAULT_ACTION_ITEMS],
    updatedAt: new Date().toISOString(),
  };
}

// Save config to localStorage
export function saveCommunicationActionBarConfig(config: CommunicationActionBarConfig): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch (e) {
    console.error("Failed to save communication action bar config:", e);
  }
}

// Update action item
export function updateCommunicationActionItem(
  config: CommunicationActionBarConfig,
  itemId: string,
  updates: Partial<Omit<CommunicationActionItem, 'id'>>
): CommunicationActionBarConfig {
  return {
    ...config,
    actionItems: config.actionItems.map(item =>
      item.id === itemId ? { ...item, ...updates } : { ...item }
    ),
    updatedAt: new Date().toISOString(),
  };
}

// Reorder action items
export function reorderCommunicationActionItems(
  config: CommunicationActionBarConfig,
  fromIndex: number,
  toIndex: number
): CommunicationActionBarConfig {
  // Sort by order first
  const orderedItems = [...config.actionItems]
    .sort((a, b) => a.order - b.order)
    .map(item => ({ ...item }));
  
  const [moved] = orderedItems.splice(fromIndex, 1);
  orderedItems.splice(toIndex, 0, moved);
  
  // Update order property
  const updatedItems = orderedItems.map((item, idx) => ({
    ...item,
    order: idx,
  }));
  
  return {
    ...config,
    actionItems: updatedItems,
    updatedAt: new Date().toISOString(),
  };
}

// Reset to defaults
export function resetCommunicationActionBarConfig(): CommunicationActionBarConfig {
  const config: CommunicationActionBarConfig = {
    actionItems: DEFAULT_ACTION_ITEMS.map(item => ({ ...item })),
    updatedAt: new Date().toISOString(),
  };
  saveCommunicationActionBarConfig(config);
  return config;
}

// Get enabled items sorted by order
export function getEnabledActionItems(config: CommunicationActionBarConfig): CommunicationActionItem[] {
  return config.actionItems
    .filter(item => item.enabled)
    .sort((a, b) => a.order - b.order);
}

// Get default items for reference
export function getDefaultCommunicationActionItems(): CommunicationActionItem[] {
  return DEFAULT_ACTION_ITEMS;
}
